/**
 * Vocabulário do PMIS: fases, saúde, status do painel, situações das atividades, do plano de ação, dos termos e das
 * entregas. Os rótulos aparecem na tela e nos relatórios; os códigos são os do banco (db/schema.sql).
 */

export const PHASES = ['iniciacao', 'planejamento', 'execucao', 'encerramento', 'finalizado', 'on_hold', 'cancelado'] as const;
export type Phase = (typeof PHASES)[number];
export const PHASE_LABEL: Record<Phase, string> = {
  iniciacao: 'Iniciação', planejamento: 'Planejamento', execucao: 'Execução', encerramento: 'Encerramento (aguardando TE)',
  finalizado: 'Finalizado', on_hold: 'On Hold', cancelado: 'Cancelado',
};
/** Fases em que o projeto conta como ativo (painel, reunião semanal, apontamento). */
export const ACTIVE_PHASES: Phase[] = ['iniciacao', 'planejamento', 'execucao', 'encerramento', 'on_hold'];

export const HEALTHS = ['saudavel', 'atencao', 'critico'] as const;
export type Health = (typeof HEALTHS)[number];
export const HEALTH_LABEL: Record<Health, string> = { saudavel: 'Saudável', atencao: 'Atenção', critico: 'Crítico' };

/** Status do painel: os 7 da planilha "ATA Projetos" e o Crítico. Calculado no banco (projects.status_dashboard). */
export const DASHBOARD_STATUSES = ['SAUDAVEL', 'ATENCAO', 'CRITICO', 'ON_HOLD', 'FALTA_TAP', 'FALTA_TE', 'FINALIZADO', 'CANCELADO'] as const;
export type DashboardStatus = (typeof DASHBOARD_STATUSES)[number];
export const DASHBOARD_STATUS_LABEL: Record<DashboardStatus, string> = {
  SAUDAVEL: 'Saudável', ATENCAO: 'Atenção', CRITICO: 'Crítico', ON_HOLD: 'On Hold', FALTA_TAP: 'Falta TAP', FALTA_TE: 'Falta TE',
  FINALIZADO: 'Finalizado', CANCELADO: 'Cancelado',
};
/** Cores fixas por status (as mesmas no painel, na lista e nos gráficos). */
export const DASHBOARD_STATUS_COLOR: Record<DashboardStatus, string> = {
  SAUDAVEL: '#22B573', ATENCAO: '#E5A50A', CRITICO: '#D64545', ON_HOLD: '#8A7CF0', FALTA_TAP: '#E8743B', FALTA_TE: '#4C8DF6',
  FINALIZADO: '#6B7280', CANCELADO: '#9CA3AF',
};

/** Mesma regra da coluna gerada no banco, para a tela calcular antes de salvar. */
export function dashboardStatus(p: { phase: Phase; health: Health; tapNumber: string | null | undefined }): DashboardStatus {
  if (p.phase === 'cancelado') return 'CANCELADO';
  if (p.phase === 'finalizado') return 'FINALIZADO';
  if (p.phase === 'on_hold') return 'ON_HOLD';
  // Entregue sem TE é o que pede ação (mesmo se a TAP nunca foi registrada).
  if (p.phase === 'encerramento') return 'FALTA_TE';
  if (!p.tapNumber) return 'FALTA_TAP';
  return p.health === 'critico' ? 'CRITICO' : p.health === 'atencao' ? 'ATENCAO' : 'SAUDAVEL';
}

export const UPDATE_KINDS = ['atualizacao', 'marco', 'reuniao', 'status', 'decisao', 'risco', 'sistema', 'importacao'] as const;
export type UpdateKind = (typeof UPDATE_KINDS)[number];
export const UPDATE_KIND_LABEL: Record<UpdateKind, string> = {
  atualizacao: 'Atualização', marco: 'Marco', reuniao: 'Reunião', status: 'Mudança de status', decisao: 'Decisão', risco: 'Risco',
  sistema: 'Sistema', importacao: 'Importado da planilha',
};
/** Tipos que a pessoa escolhe ao escrever (os demais o sistema gera). */
export const USER_UPDATE_KINDS: UpdateKind[] = ['atualizacao', 'marco', 'reuniao', 'decisao', 'risco'];

export const TASK_KINDS = ['fase', 'tarefa', 'marco'] as const;
export type TaskKind = (typeof TASK_KINDS)[number];
export const TASK_STATUSES = ['nao_iniciada', 'em_andamento', 'concluida', 'bloqueada', 'cancelada'] as const;
export type TaskStatus = (typeof TASK_STATUSES)[number];
export const TASK_STATUS_LABEL: Record<TaskStatus, string> = {
  nao_iniciada: 'Não iniciada', em_andamento: 'Em andamento', concluida: 'Concluída', bloqueada: 'Bloqueada', cancelada: 'Cancelada',
};

export const ITEM_KINDS = ['acao', 'risco', 'problema', 'decisao', 'pendencia', 'mudanca'] as const;
export type ItemKind = (typeof ITEM_KINDS)[number];
export const ITEM_KIND_LABEL: Record<ItemKind, string> = { acao: 'Ação', risco: 'Risco', problema: 'Problema', decisao: 'Decisão', pendencia: 'Pendência', mudanca: 'Pedido de mudança' };
export const CRITICALITIES = ['critica', 'alta', 'media', 'baixa'] as const;
export type Criticality = (typeof CRITICALITIES)[number];
export const CRITICALITY_LABEL: Record<Criticality, string> = { critica: 'Crítica', alta: 'Alta', media: 'Média', baixa: 'Baixa' };
export const ITEM_STATUSES = ['pendente', 'em_andamento', 'concluida', 'on_hold', 'cancelada'] as const;
export type ItemStatus = (typeof ITEM_STATUSES)[number];
export const ITEM_STATUS_LABEL: Record<ItemStatus, string> = {
  pendente: 'Pendente', em_andamento: 'Em andamento', concluida: 'Concluída', on_hold: 'On Hold', cancelada: 'Cancelada',
};

export const TERM_KINDS = ['tap', 'te_parcial', 'te'] as const;
export type TermKind = (typeof TERM_KINDS)[number];
export const TERM_KIND_LABEL: Record<TermKind, string> = { tap: 'TAP · Termo de Abertura', te_parcial: 'Termo Parcial de Aceite', te: 'TE · Termo de Encerramento' };
export const TERM_STATUSES = ['pendente', 'em_andamento', 'assinado', 'dispensado'] as const;
export type TermStatus = (typeof TERM_STATUSES)[number];
export const TERM_STATUS_LABEL: Record<TermStatus, string> = { pendente: 'Pendente', em_andamento: 'Em andamento', assinado: 'Assinado', dispensado: 'Dispensado' };
/** Etapas do Termo de Encerramento (aba "Termo de Encerramento" da planilha); editáveis em Administração → Configurações. */
export const DEFAULT_TE_STEPS: { key: string; label: string }[] = [
  { key: 'documento', label: 'Documento pronto' },
  { key: 'aprovacao_interna', label: 'Aprovação interna (Diretoria)' },
  { key: 'envio_cliente_juridico', label: 'Envio ao cliente e ao Jurídico' },
  { key: 'envio_docusign', label: 'Envio pelo DocuSign' },
  { key: 'assinatura', label: 'Assinatura' },
];

export const DELIVERY_STATUSES = ['previsto', 'em_andamento', 'lancado', 'cancelado', 'pausado'] as const;
export type DeliveryStatus = (typeof DELIVERY_STATUSES)[number];
export const DELIVERY_STATUS_LABEL: Record<DeliveryStatus, string> = {
  previsto: 'Previsto', em_andamento: 'Em andamento', lancado: 'Lançado', cancelado: 'Cancelado', pausado: 'Pausado',
};

export const COST_CATEGORIES = ['material', 'servico', 'mao_de_obra', 'viagem', 'terceiros', 'licenca', 'outros'] as const;
export type CostCategory = (typeof COST_CATEGORIES)[number];
export const COST_CATEGORY_LABEL: Record<CostCategory, string> = {
  material: 'Materiais/equipamentos', servico: 'Serviços', mao_de_obra: 'Mão de obra (horas)', viagem: 'Viagens', terceiros: 'Terceiros',
  licenca: 'Licenças/SaaS', outros: 'Outros',
};

export const MEMBER_ROLES = ['gerente', 'engenheiro', 'tecnico', 'arquiteto', 'comercial', 'observador'] as const;
export type MemberRole = (typeof MEMBER_ROLES)[number];
export const MEMBER_ROLE_LABEL: Record<MemberRole, string> = {
  gerente: 'Gerente de projeto', engenheiro: 'Engenheiro de campo', tecnico: 'Técnico', arquiteto: 'Arquiteto de soluções',
  comercial: 'Comercial', observador: 'Observador',
};

export const TIME_STATUSES = ['rascunho', 'enviado', 'aprovado', 'reprovado'] as const;
export type TimeStatus = (typeof TIME_STATUSES)[number];
export const TIME_STATUS_LABEL: Record<TimeStatus, string> = { rascunho: 'Rascunho', enviado: 'Enviado', aprovado: 'Aprovado', reprovado: 'Reprovado' };

export const TRAVEL_STATUSES = ['solicitada', 'aprovada', 'reservada', 'realizada', 'cancelada', 'recusada'] as const;
export type TravelStatus = (typeof TRAVEL_STATUSES)[number];
export const TRAVEL_STATUS_LABEL: Record<TravelStatus, string> = {
  solicitada: 'Solicitada', aprovada: 'Aprovada', reservada: 'Reservada', realizada: 'Realizada', cancelada: 'Cancelada', recusada: 'Recusada',
};

/**
 * Quem pode ser escolhido em cada papel do projeto: só quem tem o perfil (Administração › Usuários). Vale para os campos
 * do projeto, a equipe e os filtros de todas as telas. observador: qualquer pessoa.
 */
export const PROJECT_ROLE_PROFILE = { pm: 'GERENTE', tech: 'ENGENHEIRO', seller: 'VENDEDOR', architect: 'ARQUITETO' } as const;
export const MEMBER_ROLE_PROFILE: Record<MemberRole, string | null> = {
  gerente: 'GERENTE', engenheiro: 'ENGENHEIRO', tecnico: 'ENGENHEIRO', arquiteto: 'ARQUITETO', comercial: 'VENDEDOR', observador: null,
};

/**
 * Avisos por e-mail que cada pessoa liga ou desliga para si (menu do nome › Meus avisos por e-mail). Sem escolha
 * gravada, todos ficam ligados. O Administrador ainda pode desligar um aviso para todos em Configurações.
 */
export const EMAIL_NOTICES = [
  { key: 'projectAttention', label: 'Projetos que pedem atenção', description: 'Todo dia às 07:00, para quem é gerente: projetos sem registro no diário há muito tempo, aguardando o TE ou com ações críticas vencidas.' },
  { key: 'hoursReminder', label: 'Lembrete de horas', description: 'Segunda às 07:00: horas da semana passada ainda em rascunho e, para quem aprova, horas esperando aprovação.' },
  { key: 'installHolidays', label: 'Feriados na implantação', description: 'Segunda às 07:00, para quem é gerente: feriados das próximas duas semanas no estado de instalação dos seus projetos (no remoto, no estado de quem instala).' },
] as const;
export type EmailNoticeKey = (typeof EMAIL_NOTICES)[number]['key'];
export type EmailPrefs = Record<EmailNoticeKey, boolean>;
export const emailPrefs = (stored: Partial<EmailPrefs> | null | undefined): EmailPrefs =>
  Object.fromEntries(EMAIL_NOTICES.map((n) => [n.key, stored?.[n.key] !== false])) as EmailPrefs;

/** Onde a empresa reserva as viagens (Onfly). O botão "Reserve agora" de Recursos › Viagens abre este endereço (permissão travel.book). */
export const ONFLY_URL = 'https://app.onfly.com/login#/home';

/** Estados (UF): onde o projeto é instalado e onde cada pessoa trabalha (os feriados estaduais seguem o estado). */
export const UFS = [
  ['AC', 'Acre'], ['AL', 'Alagoas'], ['AP', 'Amapá'], ['AM', 'Amazonas'], ['BA', 'Bahia'], ['CE', 'Ceará'], ['DF', 'Distrito Federal'],
  ['ES', 'Espírito Santo'], ['GO', 'Goiás'], ['MA', 'Maranhão'], ['MT', 'Mato Grosso'], ['MS', 'Mato Grosso do Sul'], ['MG', 'Minas Gerais'],
  ['PA', 'Pará'], ['PB', 'Paraíba'], ['PR', 'Paraná'], ['PE', 'Pernambuco'], ['PI', 'Piauí'], ['RJ', 'Rio de Janeiro'], ['RN', 'Rio Grande do Norte'],
  ['RS', 'Rio Grande do Sul'], ['RO', 'Rondônia'], ['RR', 'Roraima'], ['SC', 'Santa Catarina'], ['SP', 'São Paulo'], ['SE', 'Sergipe'], ['TO', 'Tocantins'],
] as const;
export type Uf = (typeof UFS)[number][0];
export const UF_CODES = UFS.map(([c]) => c) as unknown as readonly [Uf, ...Uf[]];
export const UF_NAME = Object.fromEntries(UFS) as Record<Uf, string>;

/** Como o projeto é instalado. No presencial valem os feriados do estado do cliente; no remoto, os do estado de quem instala; no híbrido, os dois. */
export const INSTALL_MODES = ['presencial', 'remoto', 'hibrido'] as const;
export type InstallMode = (typeof INSTALL_MODES)[number];
export const INSTALL_MODE_LABEL: Record<InstallMode, string> = { presencial: 'Presencial (no cliente)', remoto: 'Remoto', hibrido: 'Híbrido (no cliente e remoto)' };
/** Quem instala: o engenheiro de campo do projeto e quem está na equipe como engenheiro ou técnico. */
export const INSTALLER_ROLES = ['engenheiro', 'tecnico'] as const;

/** Feriados: nacionais, estaduais e pontos facultativos vêm do feriados-brasil (sincronizados toda semana); "empresa" é cadastrado à mão. */
export const HOLIDAY_KINDS = ['nacional', 'estadual', 'facultativo', 'empresa'] as const;
export type HolidayKind = (typeof HOLIDAY_KINDS)[number];
export const HOLIDAY_KIND_LABEL: Record<HolidayKind, string> = { nacional: 'Nacional', estadual: 'Estadual', facultativo: 'Ponto facultativo', empresa: 'Empresa' };
export const FERIADOS_BRASIL_URL = 'https://github.com/joaopbini/feriados-brasil';

/** Tipos de reunião. A Executiva traz os grandes números (com os projetos por trás de cada um), os pontos tratados e o resumo por e-mail. */
export const MEETING_KINDS = ['semanal', 'executiva', 'kickoff', 'cliente', 'outra'] as const;
export type MeetingKind = (typeof MEETING_KINDS)[number];
export const MEETING_KIND_LABEL: Record<MeetingKind, string> = { semanal: 'Semanal', executiva: 'Executiva', kickoff: 'Kickoff', cliente: 'Com cliente', outra: 'Outra' };
/** Pontos tratados numa reunião executiva. Ligados a um projeto, entram no diário dele ao encerrar a reunião. */
export const MEETING_POINT_KINDS = ['decisao', 'encaminhamento', 'risco', 'informacao'] as const;
export type MeetingPointKind = (typeof MEETING_POINT_KINDS)[number];
export const MEETING_POINT_KIND_LABEL: Record<MeetingPointKind, string> = { decisao: 'Decisão', encaminhamento: 'Encaminhamento', risco: 'Risco', informacao: 'Informação' };
/** Anexos de reunião: a ata, a gravação, a apresentação e outros. */
export const MEETING_FILE_KINDS = ['ata', 'gravacao', 'apresentacao', 'outro'] as const;
export type MeetingFileKind = (typeof MEETING_FILE_KINDS)[number];
export const MEETING_FILE_KIND_LABEL: Record<MeetingFileKind, string> = { ata: 'Ata', gravacao: 'Gravação', apresentacao: 'Apresentação', outro: 'Outro' };

/** Arquivos aceitos nos anexos, pela extensão (o tipo servido no download sai daqui, nunca do navegador de quem enviou). */
export const FILE_TYPES: Record<string, string> = {
  pdf: 'application/pdf', doc: 'application/msword', docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  xls: 'application/vnd.ms-excel', xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  ppt: 'application/vnd.ms-powerpoint', pptx: 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  odt: 'application/vnd.oasis.opendocument.text', ods: 'application/vnd.oasis.opendocument.spreadsheet', odp: 'application/vnd.oasis.opendocument.presentation',
  txt: 'text/plain', md: 'text/markdown', csv: 'text/csv', vtt: 'text/vtt', srt: 'application/x-subrip',
  png: 'image/png', jpg: 'image/jpeg', jpeg: 'image/jpeg', webp: 'image/webp', gif: 'image/gif',
  vsd: 'application/vnd.visio', vsdx: 'application/vnd.ms-visio.drawing', dwg: 'image/vnd.dwg', dxf: 'image/vnd.dxf',
  mp3: 'audio/mpeg', m4a: 'audio/mp4', aac: 'audio/aac', wav: 'audio/wav', ogg: 'audio/ogg', oga: 'audio/ogg', opus: 'audio/opus', weba: 'audio/webm',
  mp4: 'video/mp4', m4v: 'video/mp4', mov: 'video/quicktime', webm: 'video/webm', zip: 'application/zip',
};
export const MAX_FILE_MB = 500;
/** Tipo do arquivo pela extensão; null se não é aceito. */
export function fileTypeFor(name: string): string | null {
  const ext = /\.([a-z0-9]{1,5})$/i.exec(name.trim())?.[1]?.toLowerCase();
  return ext ? FILE_TYPES[ext] ?? null : null;
}
